import { Mention, MentionBlot } from "quill-mention";
import "quill-mention/dist/quill.mention.css";
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from 'react-router-dom';
import { commentOnIssue, issueHistory } from "../../store/Slices/IssuesSlice";
import { getUserDropdown } from "../../store/Slices/UserSlice";
import { formatDate, formatLogAfter, getCurrentUser } from "../../utils/helper";
import { allIssuesIcon, backButtonIcon, bulletPoint, issueHistoryIcon, issuesCommentsIcon, sendMsgIcon } from '../../utils/svgs';
import { errorNotify } from "../../utils/toast";
import UserAvatar from "../../components/UserAvatar";
import ReactDOM from 'react-dom/client';

Quill.register({ "blots/mention": MentionBlot, "modules/mention": Mention });

const IssueHistory = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const currentUser = getCurrentUser();

    const { issueHistoryData } = useSelector((state) => state.issues);
    const { userDropdownData } = useSelector((state) => state.users);

    useEffect(() => {
        dispatch(getUserDropdown())
    }, [])

    const mentionUsers = (userDropdownData || []).map(u => ({ id: u.value, value: u.label, image: u.image, denotationChar: "@" }));
    const quillRef = useRef(null);

    const modules = useMemo(() => ({
        toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"]],
        mention: {
            mentionDenotationChars: ["@"],
            source: (searchTerm, renderList) => {
                const term = (searchTerm || "").toLowerCase();
                const matches = mentionUsers.filter(u => u.value.toLowerCase().includes(term));
                renderList(matches, searchTerm);
            },
            onSelect(item, insertItem) {
                insertItem(item);

                const quill = quillRef.current?.getEditor();
                if (quill) {
                    const range = quill.getSelection(true);
                    quill.insertText(range.index, " ");
                    quill.setSelection(range.index + 1, 0);
                }
            },
            renderItem: (item) => {
                const div = document.createElement('div');
                div.style.display = 'flex';
                div.style.alignItems = 'center';
                div.style.gap = '8px';
                div.style.padding = '4px 8px';
                const avatarContainer = document.createElement('div');
                ReactDOM.createRoot(avatarContainer).render(
                    <UserAvatar username={item.value} image={item.image} size={24} />
                );
                const span = document.createElement('span');
                span.textContent = item.value;
                div.appendChild(avatarContainer);
                div.appendChild(span);
                return div;
            },
        },
    }), [mentionUsers]);

    const formats = [
        "bold", "italic", "underline",
        "list", "bullet",
        "link",
        "mention",
    ];

    const [tab, setTab] = useState("all");
    const [commentValue, setCommentValue] = useState(null);
    const [parentId, setParentId] = useState(null);
    const [sending, setSending] = useState(false);
    const [threadComment, setThreadComment] = useState(null);
    const [threadReplies, setThreadReplies] = useState([]);

    const extractMentionUserIds = (delta) => {
        const ids = [];
        if (!delta || !delta.ops) return ids;
        delta.ops.forEach(op => {
            if (op.insert && op.insert.mention && op.insert.mention.id) {
                ids.push(op.insert.mention.id);
            }
        });
        return Array.from(new Set(ids));
    };

    const handleSendComment = async () => {
        if (!commentValue.trim()) return;
        else if (commentValue === "<p><br></p>") return;
        setSending(true);
        try {
            const quill = quillRef.current?.getEditor();
            const delta = quill ? quill.getContents() : null;
            const mention_user_ids = extractMentionUserIds(delta);
            const payload = {
                comment: commentValue,
                service_issue_id: parseInt(id),
                parent_id: parentId ? parseInt(parentId) : 0,
                mention_user_ids
            };

            await dispatch(commentOnIssue(payload)).unwrap();
            setCommentValue("");
            setParentId(null);
            setThreadComment(null);
            setThreadReplies([])
            dispatch(issueHistory({ history_type: tab, service_issue_id: id }));
        } catch (err) {
            errorNotify(err);
        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        const params = {
            history_type: tab,
            service_issue_id: id
        }
        dispatch(issueHistory(params))
    }, [tab, id, dispatch])

    const handleOpenThread = (commentItem) => {
        setThreadComment(commentItem);
        setThreadReplies(commentItem.comments?.parent || []);
        setParentId(commentItem.comments?.id);
    };

    const handleBackFromThread = () => {
        setThreadComment(null);
        setThreadReplies([]);
        setParentId(null);
    };

    return (
        <div className="issue_history_wrapper">
            <div className='issue_details_tabs'>
                <div onClick={() => {
                    setThreadComment(null);
                    setThreadReplies([]);
                    setTab("all")
                }} className={tab === "all" ? "active" : ""}>{allIssuesIcon} All</div>
                <div onClick={() => {
                    setThreadComment(null);
                    setThreadReplies([]);
                    setTab("comment")
                }} className={tab === "comment" ? "active" : ""}>{issuesCommentsIcon} Comments</div>
                <div onClick={() => {
                    setThreadComment(null);
                    setThreadReplies([]);
                    setTab("logs")
                }} className={tab === "logs" ? "active" : ""}>{issueHistoryIcon} History</div>
            </div>

            <div className="issue_details_right_side">
                {threadComment ? (
                    <>
                        <div className="thread_window">
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, justifyContent: "center", position: "relative" }}>
                                <button className="back_btn" onClick={handleBackFromThread} style={{ marginRight: 12 }}>{backButtonIcon}</button>
                                <div className="thread_title">
                                    Thread by
                                    <span style={{ fontWeight: 600, textTransform: 'capitalize', marginLeft: "5px" }}>
                                        <UserAvatar username={threadComment.comments?.user?.username} image={threadComment.comments?.user?.image} />
                                        {threadComment.comments?.user?.username || '-'}</span>
                                </div>
                            </div>
                            {/* Original comment */}
                            <div className='issues_comments_wrapper' style={{ marginBottom: 16 }}>
                                <div>
                                    <div>
                                        <UserAvatar username={threadComment.comments?.user?.username} image={threadComment.comments?.user?.image} />
                                        <h6>{threadComment.comments?.user?.username || '-'}</h6>
                                    </div>
                                    <div><p>{formatDate(threadComment.created_at)}</p></div>
                                </div>
                                <div>
                                    <p dangerouslySetInnerHTML={{ __html: threadComment.comments?.comment || '-' }} />
                                </div>
                            </div>
                            {/* Replies */}
                            {threadReplies.map((reply, idx) => (
                                <div className='issues_comments_wrapper' key={reply.id || idx}>
                                    <div>
                                        <div>
                                            <UserAvatar username={reply?.user?.username} image={reply?.user?.image} />
                                            <h6>{reply?.user?.username || '-'}</h6>
                                        </div>
                                        <div><p>{formatDate(reply.created_at)}</p></div>
                                    </div>
                                    <div>
                                        <p dangerouslySetInnerHTML={{ __html: reply?.comment || '-' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className='issue_comment_input' style={{ marginTop: 24 }}>
                            <ReactQuill
                                placeholder={"Reply to comment..."}
                                modules={modules}
                                ref={quillRef}
                                theme="snow"
                                formats={formats}
                                name={"comment"}
                                className='react_quill_input quill-small-font'
                                value={commentValue}
                                onChange={setCommentValue}
                            />
                            <span className='send_icon' style={{ cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.5 : 1 }} onClick={sending ? undefined : handleSendComment}>{sendMsgIcon}</span>
                        </div>
                    </>
                ) : (
                    <>
                        <div>
                            {(() => {
                                if (!Array.isArray(issueHistoryData) || issueHistoryData.length === 0) {
                                    if (tab === 'logs') return <div className="no-items text-center">No logs found</div>
                                    if (tab === 'comment') return <div className="no-items text-center">No comments found</div>
                                    return <div className="no-items text-center">No history found</div>
                                }
                                const filtered = issueHistoryData.filter(item =>
                                    tab === 'all' ? true :
                                        tab === 'comment' ? item.source_type === 'comment' :
                                            tab === 'logs' ? item.source_type === 'log' : true
                                );
                                if (filtered.length === 0) {
                                    if (tab === 'logs') return <div className="no-items text-center">No logs found</div>
                                    if (tab === 'comment') return <div className="no-items text-center">No comments found</div>
                                    return <div className="no-items text-center">No history found</div>
                                }
                                return filtered.map((item, idx) => (
                                    item.source_type === 'log' ? (
                                        <div className='issue_activity_logs' key={item.id || idx}>
                                            <div>
                                                {bulletPoint}
                                                <div>
                                                    {item.logs && item.logs.before && item.logs.after ? (
                                                        <span>
                                                            <span dangerouslySetInnerHTML={{ __html: formatLogAfter(item.logs.before, currentUser) }} />
                                                            <span style={{ margin: '0 4px', fontWeight: 600, color: "#4F19E2" }}>to</span>
                                                            <span dangerouslySetInnerHTML={{ __html: formatLogAfter(item.logs.after, currentUser) }} />
                                                        </span>
                                                    ) : item.logs && item.logs.before ? (
                                                        <span dangerouslySetInnerHTML={{ __html: formatLogAfter(item.logs.before, currentUser) }} />
                                                    ) : item.logs && item.logs.after ? (
                                                        <span dangerouslySetInnerHTML={{ __html: formatLogAfter(item.logs.after, currentUser) }} />
                                                    ) : '-'}
                                                </div>
                                            </div>
                                            <div>{formatDate(item.created_at)}</div>
                                        </div>
                                    ) : item.source_type === 'comment' ? (
                                        <div className='issues_comments_wrapper' key={item.id || idx}>
                                            <div>
                                                <div>
                                                    <UserAvatar username={item.comments?.user?.username} image={item.comments?.user?.image} />
                                                    <h6>{item.comments?.user?.username || '-'}</h6>
                                                </div>
                                                <div><p>{formatDate(item.created_at)}</p></div>
                                            </div>
                                            <div>
                                                <p dangerouslySetInnerHTML={{ __html: item.comments?.comment || '-' }} />
                                            </div>
                                            {
                                                item.comments?.parent?.length > 0 ?
                                                    <div className='issue_comment_reply' onClick={() => handleOpenThread(item)} style={{ cursor: 'pointer' }}>
                                                        <p>{item.comments?.parent?.length} Reply
                                                            {(() => {
                                                                const seen = new Set();
                                                                return item.comments?.parent?.filter(users => {
                                                                    if (!users?.user?.id) return false;
                                                                    if (seen.has(users.user.id)) return false;
                                                                    seen.add(users.user.id);
                                                                    return true;
                                                                }).map((users) => (
                                                                    <UserAvatar username={users?.user?.username} image={users?.user?.image} />
                                                                ));
                                                            })()}
                                                        </p>
                                                    </div> :
                                                    <div className='issue_comment_reply' onClick={() => handleOpenThread(item)} style={{ cursor: 'pointer' }}>
                                                        <p>Reply</p>
                                                    </div>
                                            }
                                        </div>
                                    ) : null
                                ));
                            })()}
                        </div>
                        <div className='issue_comment_input'>
                            <ReactQuill
                                placeholder={"Write a comment..."}
                                modules={modules}
                                ref={quillRef}
                                theme="snow"
                                formats={formats}
                                name={"comment"}
                                className='react_quill_input quill-small-font'
                                value={commentValue}
                                onChange={setCommentValue}
                            />
                            <span className='send_icon' style={{ cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.5 : 1 }} onClick={sending ? undefined : handleSendComment}>{sendMsgIcon}</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default IssueHistory